import React, { useEffect } from 'react'
import { onPageEnter } from 'lib/tracking/pageEnter'
import styled from 'styled-components'
import Knower from 'assets/become-knower/knower.svg'
import FunctionIcon1 from 'assets/become-knower/function-icon1.svg'
import FunctionIcon2 from 'assets/become-knower/function-icon2.svg'
import FunctionIcon3 from 'assets/become-knower/function-icon3.svg'
import FunctionIcon4 from 'assets/become-knower/function-icon4.svg'
import Leaf from 'assets/become-knower/left-leaf.svg'
import Arrow from 'assets/become-knower/arrow.svg'
import Meta from 'components/modules/SEO/Meta'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NewButtonLink } from 'components/elements/NewButton'
import theme from 'stories/utils/theme'
import { ButtonText, Headline4, Subtitle1 } from 'stories/elements/Typography/Text'
import { useTranslation } from 'next-i18next'
import { parseTranslationInterpolation } from 'lib/features/translation'
import { knowCreateRoute } from 'lib/constants/routes'
import { useRouter } from 'next/router'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import { parseLocale } from 'lib/constants/localization'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventBecomeKnowerPageView } from 'lib/constants/AnalyticsEventType'

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 100px;
    @media (max-width: 750px) {
        grid-gap: 20px;
    }
`
const FirstContainer = styled.div`
    background: ${theme.colors.backgroundColorGray};
`

const FirstContent = styled.section`
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 500px;
    & .caption-text {
        font-size: 11px;
        font-weight: bold;
        letter-spacing: 1.2px;
        line-height: 16px;
        opacity: 0.7;
    }

    & h1 {
        font-size: 36px;
        font-weight: bold;
        line-height: 55px;
        margin-top: 20px;
        text-align: left;
    }
    & span {
        font-size: 36px;
        color: #65a0f1;
        font-weight: bold;
    }
    & .sub-heading {
        font-weight: 570;
        font-size: 16px;
        line-height: 24px;
        color: #919599;
        margin-bottom: 40px;
    }

    @media (max-width: 750px) {
        height: 500px;
        display: grid;
        place-content: start center;
        grid-template-columns: 1fr;

        svg {
            display: none;
        }
        & div {
            width: 100%;
        }
        p,
        h1 {
            text-align: center;
        }
    }
`

const SecondContainer = styled.div`
    & section {
        padding-bottom: 0px;
    }

    & p {
        font-weight: 600;
        font-size: 22px;
    }
`

const SecondContent = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 0.3fr);
    height: 200px;
    h5 {
        color: ${theme.colors.body2Black};
        text-align: center;
    }

    & .custom-line {
        border: 2px dashed ${theme.colors.body2Black};
        display: flex;
        place-content: center;
        margin-top: 60px;
    }

    @media (max-width: 750px) {
        max-width: 100%;
        grid-template-columns: 1fr;
        text-align: center;
        height: auto;
        & .custom-line {
            border: none;
        }
        & .line-center {
            height: 15px;
        }
    }
`
const ThirdContainer = styled.div`
    background-color: ${theme.colors.backgroundColorGray};

    & section {
        padding-bottom: 0px;
        padding-top: 0px;
        display: grid;
        grid-gap: 75px;
    }

    & h2 {
        margin-top: 0px;
        font-weight: 600;
        font-size: 30px;
    }

    @media (max-width: 1050px) {
        max-width: 100%;

        & section {
            margin: 0px;
            padding: 0 15px;
        }
        .heading {
            text-align: center;
        }
    }
`

const ThirdContent = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-gap: 20px;

    & .heading {
        font-weight: 600;
        font-size: 22px;
    }

    & .description {
        font-weight: 600;
        font-size: 15px;
        color: ${theme.colors.body2Black};
    }

    & .card {
        width: 100%;
        height: 100%;
        background: hsla(0, 0%, 100%, 0.89);
        box-shadow: 5px 10px 100px rgba(7, 7, 7, 0.05);
        border-radius: 19.5626px;
        padding: 27px;
    }

    & .small-card {
        display: grid;
        grid-template-columns: 0.2fr 0.8fr;
        width: 340px;
        height: 70px;
        background: rgba(255, 255, 255, 0.89);
        box-shadow: 5px 10px 100px rgba(7, 7, 7, 0.05);
        border-radius: 10px;
        padding: 7px;
        & p {
            font-weight: 600;
            font-size: 15px;
        }
    }
    @media (max-width: 750px) {
        max-width: 100%;
        grid-template-columns: 1fr;
        align-items: center;
        & .card {
            width: 100%;
            height: 100%;
        }
    }
`

const FourthContent = styled.div`
    & .rectangle-box {
        display: grid;
        grid-template-columns: 0.5fr 0.2fr 0.3fr;
        grid-gap: 30px;
        width: 100%;
        height: 100%;
        background: ${theme.colors.deepSeaBlue};
        box-shadow: 5px 10px 100px rgba(7, 7, 7, 0.05);
        border-radius: 19.5626px;
        & .rectangle-heading {
            font-weight: 600;
            font-size: 35px;
            padding: 0px 70px 0px 70px;
            color: ${theme.colors.white};
        }
        & .rectangle-description {
            font-weight: 600;
            font-size: 15px;
            line-height: 22px;
            color: rgba(255, 255, 255, 0.7);
            padding: 10px 70px;
        }
    }

    & .button-div {
        text-align: center;
        display: grid;
        place-items: center;
    }

    @media (max-width: 750px) {
        max-width: 100%;

        p {
            text-align: center;
        }
        & .rectangle-box {
            width: 100%;
            height: 100%;
            grid-template-columns: 1fr;
            display: grid;
            place-items: center;

            & svg {
                display: none;
            }
        }

        & .button-div {
            padding: 20px 0;
        }
    }
`

const BecomeKnower = () => {
    const { t } = useTranslation('becomeKnower')
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)
    const countryLanguageConfig = getCountryLanguageConfig(locale)

    useEffect(() => {
        onPageEnter()
        trackEvent(AnalyticsEventBecomeKnowerPageView, {})
    }, [])

    if (!countryLanguageConfig) return <></>

    return (
        <Container>
            <Meta title={t('becomeKnower/pageTitle')} description={t('becomeKnower/pageDescription')} />
            <FirstContainer>
                <FirstContent>
                    <div className="responsive-style">
                        <p className="caption-text">{t('becomeKnower/headerCaption')}</p>
                        <h1>
                            {parseTranslationInterpolation(t('becomeKnower/pageHeader'), [
                                { index: 1, component: 'span', props: {} },
                            ])}
                        </h1>
                        <p className="sub-heading">{t('becomeKnower/headerSubtitle')}</p>

                        <NewButtonLink
                            fullWidth={false}
                            href={knowCreateRoute}
                            width={325}
                            height={50}
                            borderRadius={theme.borderRadius.normal}
                        >
                            <ButtonText color={theme.colors.white}>{t('becomeKnower/headerCTA')}</ButtonText>
                        </NewButtonLink>
                    </div>

                    <div className="responsive-style">
                        <Knower />
                    </div>
                </FirstContent>
            </FirstContainer>
            <SecondContainer>
                <section>
                    <Headline4 textAlign="center" color={theme.colors.headlineDark}>
                        {t('becomeKnower/explanantionHeader')}
                    </Headline4>
                    <SecondContent>
                        <div>
                            <FunctionIcon1 />
                            <Subtitle1>{t('becomeKnower/explanantionText1')}</Subtitle1>
                        </div>
                        <div className="line-center">
                            <hr className="custom-line" />
                        </div>
                        <div>
                            <FunctionIcon2 />
                            <Subtitle1>{t('becomeKnower/explanantionText2')}</Subtitle1>
                        </div>
                        <div className="line-center">
                            <hr className="custom-line" />
                        </div>
                        <div>
                            <FunctionIcon3 />
                            <Subtitle1>{t('becomeKnower/explanantionText3')}</Subtitle1>
                        </div>
                        <div className="line-center">
                            <hr className="custom-line" />
                        </div>
                        <div>
                            <FunctionIcon4 />
                            <Subtitle1>{t('becomeKnower/explanantionText4')}</Subtitle1>
                        </div>
                    </SecondContent>
                </section>
            </SecondContainer>
            <ThirdContainer>
                <Leaf />
                <section>
                    <Headline4 textAlign="center" color={theme.colors.headlineDark}>
                        {t('becomeKnower/advantagesHeader')}
                    </Headline4>
                    <ThirdContent>
                        <div className="card">
                            <p className="heading">{t('becomeKnower/advantageTitle1')}</p>
                            <p className="description">{t('becomeKnower/advantageText1')}</p>
                        </div>
                        <div className="card">
                            <p className="heading">{t('becomeKnower/advantageTitle2')}</p>
                            <p className="description">{t('becomeKnower/advantageText2')}</p>
                        </div>
                    </ThirdContent>
                    <FourthContent>
                        <div className="rectangle-box">
                            <div>
                                <p className="rectangle-heading">{t('becomeKnower/becomeTutorTitle')}</p>
                                <p className="rectangle-description">{t('becomeKnower/becomeTutorText')}</p>
                            </div>
                            <div>
                                <Arrow />
                            </div>
                            <div className="button-div">
                                <NewButtonLink
                                    fullWidth={false}
                                    href={knowCreateRoute}
                                    borderRadius={theme.borderRadius.normal}
                                    height={75}
                                    color={theme.colors.flamingoPink}
                                    width={250}
                                >
                                    <ButtonText color={theme.colors.white}>{t('becomeKnower/becomeKnowerCTA')}</ButtonText>
                                </NewButtonLink>
                            </div>
                        </div>
                    </FourthContent>
                </section>
            </ThirdContainer>
        </Container>
    )
}

export const getStaticProps: GetStaticProps = async (props) => ({
    props: {
        ...(await serverSideTranslations(parseLocale(props.locale).interfaceLanguageCode, ['common', 'becomeKnower'])),
    },
})

export default BecomeKnower
